import { useNavigate, useLocation } from "react-router-dom";
import { forwardRef, type ButtonHTMLAttributes } from "react";

export type BackButtonVariant = "default" | "exit" | "light";

export interface BackButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /** Visual variant: 'default' for navigation, 'exit' for game challenges */
  variant?: BackButtonVariant;
  /** Custom navigation target (defaults to browser history back) */
  to?: string;
}

/**
 * BackButton - Consistent back navigation component.
 *
 * Features:
 * - 64px+ touch target for preschool accessibility
 * - Smart navigation (uses logical parent pages when possible)
 * - Two variants: default (arrow) and exit (X for challenges)
 * - Consistent placement in top-left corner
 *
 * Navigation Logic:
 * - /play/:discipline/:level -> /play/:discipline
 * - /play/:discipline -> /play
 * - /challenge/:id -> /play (main hub)
 * - Others -> browser history back
 *
 * @example
 * // Standard back button
 * <BackButton />
 *
 * @example
 * // Exit button for challenges
 * <BackButton variant="exit" />
 *
 * @example
 * // Custom navigation target
 * <BackButton to="/play" />
 */
export const BackButton = forwardRef<HTMLButtonElement, BackButtonProps>(
  ({ variant = "default", to, className = "", ...props }, ref) => {
    const navigate = useNavigate();
    const location = useLocation();

    const handleClick = () => {
      if (to) {
        navigate(to);
        return;
      }

      // Smart navigation based on current route
      const path = location.pathname;

      // Challenge page -> go to play hub
      if (path.startsWith("/challenge/")) {
        navigate("/play");
        return;
      }

      // Level page -> go to discipline page
      // e.g., /play/math/1 -> /play/math
      if (path.match(/^\/play\/[^/]+\/\d+$/)) {
        const parts = path.split("/");
        navigate(`/play/${parts[2]}`);
        return;
      }

      // Discipline page -> go to play hub
      // e.g., /play/math -> /play
      if (path.match(/^\/play\/[^/]+$/)) {
        navigate("/play");
        return;
      }

      // Default: go back in browser history
      navigate(-1);
    };

    const baseStyles = `
      inline-flex items-center justify-center
      min-w-[64px] min-h-[64px]
      rounded-full
      font-display font-bold
      cursor-pointer
      select-none
      touch-manipulation
      transition-all duration-200
      focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-400 focus-visible:ring-offset-2
      active:scale-95
    `;

    const variantStyles = {
      default: `
        bg-cloud
        text-magic-600
        border-[3px] border-magic-300
        shadow-md
        hover:bg-magic-50 hover:border-magic-400
        active:shadow-sm
      `,
      exit: `
        bg-cloud/90 backdrop-blur-sm
        text-neutral-600
        border-[3px] border-neutral-300
        shadow-lg
        hover:bg-neutral-100 hover:border-neutral-400
        active:shadow-md
      `,
      light: `
        bg-white/20 backdrop-blur-sm
        text-white
        border-[3px] border-white/40
        shadow-lg
        hover:bg-white/30 hover:border-white/60
        active:shadow-md
      `,
    };

    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        aria-label={variant === "exit" ? "Exit challenge" : "Go back"}
        className={`
          ${baseStyles}
          ${variantStyles[variant]}
          ${className}
        `}
        {...props}
      >
        {variant === "exit" ? <CloseIcon /> : <ArrowLeftIcon />}
      </button>
    );
  }
);

BackButton.displayName = "BackButton";

function ArrowLeftIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  );
}

export default BackButton;
